/**
 * LinkDateTimePicker — inline link-styled trigger that opens MUI X's
 * DateTimePicker dialog.
 *
 * Feature-scoped to the AssignTeammatesDrawer / escalations flow. Visual
 * spec is locked to that design (see
 * `.claude/projects/tickets/escalations/design-specs/start-end-select`),
 * so this is intentionally NOT promoted to `src/components/input/`. If a
 * second consumer ever needs the inline-link trigger pattern, generalize
 * the API (renderTrigger / styling tokens) and lift then.
 *
 * Implementation notes:
 *   - The trigger is a plain `<Box ref={triggerRef}>` — NOT a MUI X
 *     `slots.field`. We tried slots.field and it forced cross-component
 *     prop forwarding (Context) just to satisfy MUI X's slotProps typing.
 *     Rendering the trigger ourselves and pointing the popper at our ref
 *     via `slotProps.popper.anchorEl` is simpler and uses only local
 *     `useState` / `useRef`.
 *   - `slots.field` is set to a no-op component so MUI X doesn't render
 *     its default TextField (which would visually conflict with our
 *     inline-link trigger).
 */

import { Box, Typography, useTheme } from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useGlobalStore } from "controller/store/globalStore";
import type { Dayjs } from "dayjs";
import { type KeyboardEvent, useState } from "react";
import { dayjs } from "utilz";
import { nextWholeMinute } from "../utils/timeSlotDraft";

export interface LinkDateTimePickerProps {
  value: Dayjs | string | null;
  timezone: string;
  disabled?: boolean;
  error?: boolean;
  minDateTime?: Dayjs | string | null;
  // Blocks selecting any past date AND time (resource-tz "now", via the
  // `timezone` prop). Used so a schedule can't start/end in the past.
  disablePast?: boolean;
  placeholder: string;
  ariaLabel: string;
  onChange: (value: Dayjs | null) => void;
}

// No-op field — keeps MUI X from rendering its default TextField. Defined
// at module scope so component identity is stable across renders (avoids
// MUI X re-mounting the field each render).
const NoopField = () => null;

// Empty → UNCONTROLLED with the "now" seed as defaultValue: MUI X only accepts
// an untouched default on OK when uncontrolled (a controlled seed counts as
// already committed, so OK would no-op), and dismissing leaves it unset.
// `key` remounts the picker per seed / mode switch.
const getValueMode = (value: Dayjs | null, seed: Dayjs | null) =>
  value
    ? { key: "value", props: { value } }
    : {
        key: `seed-${seed?.valueOf() ?? "none"}`,
        props: { defaultValue: seed },
      };

export const LinkDateTimePicker = ({
  value,
  timezone,
  disabled = false,
  error = false,
  minDateTime,
  disablePast = false,
  placeholder,
  ariaLabel,
  onChange,
}: LinkDateTimePickerProps) => {
  const theme = useTheme();
  const dateFormat =
    useGlobalStore((state) => state.dateFormat) || "MM/DD/YYYY";
  const [open, setOpen] = useState(false);
  // State (not ref) so we can pass it directly to MUI X's `popper.anchorEl`,
  // which accepts `HTMLElement | null` but not a callback returning null.
  // `useState`'s setter has stable identity so it's safe as a ref callback.
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const dayjsValue = value ? dayjs(value) : null;
  const hasValue = Boolean(dayjsValue?.isValid());
  const formattedText = hasValue
    ? (dayjsValue?.format(`${dateFormat} h:mm A`) ?? "")
    : "";

  // Color rules:
  //   default       → lightBg.low (placeholder + filled)
  //   hover / open  → primary.main + underline (baked into link-small)
  //   error         → error.main in BOTH default and hover
  const defaultColor = error
    ? theme.palette.error.main
    : theme.palette.lightBg.low;
  const activeColor = error
    ? theme.palette.error.main
    : theme.palette.primary.main;

  // An EMPTY picker opens with "now" (resource tz) pre-selected INSIDE the
  // dialog only — nothing is written to the row until the user presses OK (or
  // finishes picking). For a Start picker the parent's applySlotPatch then
  // defaults End to Start + 1h.
  const [seed, setSeed] = useState<Dayjs | null>(null);
  const valueMode = getValueMode(hasValue ? dayjsValue : null, seed);
  const openPicker = () => {
    if (disabled) return;
    if (!hasValue) setSeed(nextWholeMinute(timezone));
    setOpen(true);
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openPicker();
    }
  };

  // When `open`, force the active style — matches locked rule "stays in
  // hover state while picker dialog is open".
  return (
    <>
      <Box
        ref={setAnchorEl}
        onClick={openPicker}
        onKeyDown={handleKeyDown}
        role="button"
        aria-label={ariaLabel}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : 0}
        sx={{
          // Locked width — keeps the trigger Box at a constant size across
          // empty / filled / hover / open states. Without this, the Box grows
          // with text length (empty placeholder is narrower than a filled
          // date, and hover swaps the font to SegoeSemibold which widens it
          // further), which visually shifts the arrow icon sitting between
          // two pickers and the right-cluster duration / clock / copy /
          // delete column.
          minWidth: "168px",
          p: "2px 16px",
          borderRadius: "4px",
          cursor: disabled ? "default" : "pointer",
          transition: "background-color 0.15s ease",
          backgroundColor: open ? theme.palette.neutral["005"] : "transparent",
          "& .picker-text": {
            ...theme.typography["body-medium"],
            color: defaultColor,
            textDecoration: "none",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "block",
            minWidth: 0,
          },
          ...(open
            ? {
                "& .picker-text": {
                  ...theme.typography["link-small"],
                  color: activeColor,
                  textDecoration: "underline",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                  minWidth: 0,
                },
              }
            : !disabled && {
                "&:hover": {
                  backgroundColor: theme.palette.neutral["005"],
                  "& .picker-text": {
                    ...theme.typography["link-small"],
                    color: activeColor,
                    textDecoration: "underline",
                  },
                },
                "&:focus-visible": {
                  backgroundColor: theme.palette.neutral["005"],
                  outline: "none",
                },
              }),
        }}
      >
        <Typography component="span" className="picker-text" textAlign="center">
          {hasValue ? formattedText : placeholder}
        </Typography>
      </Box>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateTimePicker
          key={valueMode.key}
          {...valueMode.props}
          open={open}
          onClose={() => setOpen(false)}
          disabled={disabled}
          minDateTime={minDateTime ? dayjs(minDateTime) : undefined}
          disablePast={disablePast}
          timezone={timezone}
          format={`${dateFormat} h:mm A`}
          ampm
          ampmInClock
          closeOnSelect
          onChange={(newValue, context) => {
            if (hasValue && context?.validationError == null) {
              onChange(newValue);
            }
          }}
          onAccept={(newValue) => {
            if (!hasValue && newValue) onChange(newValue);
          }}
          slots={{ field: NoopField }}
          slotProps={{
            popper: {
              placement: "bottom-start",
              anchorEl,
            },
            actionBar: {
              sx: {
                "&.MuiDialogActions-root": {
                  color: `${theme.palette.purple.main} !important`,
                  "& .MuiButtonBase-root": {
                    ...theme.typography.caption,
                    color: `${theme.palette.purple.main} !important`,
                  },
                },
              },
            },
            desktopPaper: {
              sx: {
                "& .MuiPickersDay-root": {
                  borderRadius: "6px",
                  ":hover": {
                    bgcolor: theme.palette.neutral["005"],
                  },
                  ...theme.typography["body-small"],
                  "&.Mui-selected": {
                    backgroundColor: theme.palette.purple.main,
                    color: "#FFF",
                  },
                  "&.MuiPickersDay-today": {
                    border: `1px solid ${theme.palette.purple.main}`,
                  },
                },
                "& .MuiMultiSectionDigitalClock-root": {
                  ...theme.typography["body-medium"],
                  "& .Mui-selected": {
                    backgroundColor: "#EBFCFE",
                    border: `1px solid ${theme.palette.purple.main}`,
                    color: theme.palette.purple.main,
                    borderRadius: "6px",
                  },
                },
                borderRadius: "16px",
                boxShadow: "0px 3px 14px 2px rgba(0, 0, 0, 0.12)",
              },
            },
          }}
        />
      </LocalizationProvider>
    </>
  );
};
